import React from 'react';
import { observer } from 'mobx-react';
import { computed, observable } from 'mobx';
import { ExerciseStore, ExerciseTypeStore, WorksheetStore } from '@seedlang/stores';
import InPlaceText from 'components/form/in_place_text';
import autobind from 'autobind-decorator';
import { Link } from 'react-router';
import InPlaceSelect from 'components/form/in_place_select';
import { isBlank, isPresent } from '@seedlang/utils';
import ExerciseEntryIndex from 'pages/creator/exercise_entry/exercise_entry_index';
import ExerciseEntryCreate from 'pages/creator/exercise_entry/exercise_entry_create';
import Modal from 'components/modal';
import TagEdit from 'pages/creator/tags/tag_edit';
import RatingIndex from 'pages/builder/ratings/rating_index';
import { noop, some } from 'lodash';
import Alert from 'components/alert';
import Spinner from 'components/spinner';
import Text from 'components/text';
import styled from '@emotion/styled';
import { AppUI, ExerciseUI } from '@seedlang/state';
import InPlaceCheckbox from 'components/form/in_place_checkbox';
import InfoTooltip from 'components/info_tooltip';
import ReactTooltip from 'react-tooltip';
import { Constants } from '@seedlang/constants';
import Stack from 'components/layout/stack';
import Grid from 'components/layout/grid';
import GrayButton from 'components/button/gray_button';
import ButtonPrimary from 'components/button/button_primary';
import PageTitle from 'components/common/page_title';

const NavigationButtonRow = styled.div`
  display: flex;
  gap: 10px;
  margin-top: 5px;
`;

const EntriesSettingsRow = styled.div`
  display: flex;
  gap: 10px;
  margin-bottom: 10px;
  align-items: center;
  justify-content: space-between;
`;

const ButtonBorder = styled.div`
  border-bottom: 1px solid #C5C5C5;
  padding-bottom: 10px;
  margin-bottom: 10px;
`;

const TagSection = styled.div`
  display: flex;
`;

const TagWrapper = styled.div`
  flex: 1;
`;

const Tag = styled.div`
  border: 1px solid gray;
  border-radius: 5px;
  margin: 5px;
  background: white;
  display: inline-block;
  padding: 5px 10px;
  font-size: 14px;
`;

@observer
class ExerciseEdit extends React.Component {
  @observable editTags = false;
  @observable showRatingComments = false;
  @observable randomizing = false;

  constructor(props) {
    super(props);
    ExerciseTypeStore.getIndex();
    WorksheetStore.getShow({ids: {worksheetId: this.props.params.worksheetId}});
    this.getExercise();
  }

  componentDidUpdate(prevProps) {
    if (this.props.params?.exerciseId !== prevProps.params?.exerciseId) {
      ExerciseStore.clearShowData();
      this.getExercise();
    }
  }

  componentWillUnmount() {
    ExerciseStore.clearShowData();
  }

  @autobind getExercise() {
    ExerciseUI.exerciseId = this.props.params.exerciseId;
    ExerciseStore.getShow({ids: {exerciseId: this.props.params.exerciseId}}, this.afterGetExercise);
  }

  @autobind afterGetExercise(resp) {
    ExerciseStore.setShowData(resp);
    this.randomizing = false;
  }

  @autobind clearField(field) {
    const data = {};
    data[field] = null;
    ExerciseStore.update({ids: {exerciseId: this.props.params.exerciseId}, data: data}, this.getExercise);
  }

  @computed get hasInvalidExerciseEntry() {
    return ExerciseStore.hasShowData && !ExerciseStore.showData.exerciseType?.hideTarget && some(ExerciseStore.showData.exerciseEntries.map(item => isBlank(item.sentence)));
  }

  @computed get disableChangingExerciseType() {
    return ExerciseStore.showData?.exerciseEntriesCount > 0;
  }

  @autobind randomizeOrder() {
    if (ExerciseStore.showData.exerciseEntriesCount < 2) { return; }
    this.randomizing = true;
    ExerciseStore.randomizeEntries({ids: {exerciseId: this.props.params.exerciseId}}, this.getExercise);
  }

  @computed get optionsDisplayedOptions() {
    const options = [['null', `All (${ExerciseStore.showData.selectedUniqueWords.length})`]];
    if (ExerciseStore.hasShowData && isPresent(ExerciseStore.showData.wordAssociationsWithExtraWords)) {
      ExerciseStore.showData.selectedUniqueWords.slice(0, -1).forEach((wordAssociation, index) => {
        if (index > 0) {
          options.push([index + 1, (index + 1).toString()]);
        }
      });
    }
    return options;
  }

  render() {
    return (
      <Stack gap='xs' alignItems='stretch'>
        <PageTitle>{WorksheetStore.showData?.name}</PageTitle>
        {
          this.editTags &&
            <Modal
              onCloseModal={() => this.editTags = false}
              height='550px'
            >
              <TagEdit
                groupId={AppUI.groupId}
                exercise={ExerciseStore.showData}
                onChange={this.getExercise}
              />
            </Modal>
        }
        {
          this.showRatingComments &&
            <Modal
              onCloseModal={() => this.showRatingComments = false}
              height='550px'
            >
              <RatingIndex
                simple
                defaultFilters={{group_id: AppUI.groupId, exercise_id: this.props.params.exerciseId, comment: true}}
              />
            </Modal>
        }
        <div className='breadcrumbs'>
          <div>
            <Link
              to={{name: 'creator.worksheets.index', params: {groupId: AppUI.groupId}}}
            >
              Posts
            </Link>
          </div>
          <i className='fa fa-angle-double-right' />
          {
            WorksheetStore.hasShowData &&
            <div>
              <Link
                to={{name: 'creator.worksheets.edit', params: {worksheetId: WorksheetStore.showData.id, groupId: AppUI.groupId}}}
              >
                {WorksheetStore.showData.name}
              </Link>
            </div>
          }
          <i className='fa fa-angle-double-right' />
          <div>
            {ExerciseStore.showData.name}
          </div>
        </div>
        {
          this.hasInvalidExerciseEntry &&
            <Alert margin='10px 0'><i className='fa fa-warning' /> This exercise has an invalid exercise entry.</Alert>
        }
        <Grid columns={[2,1,1]}>
          <fieldset style={{height: '100%'}}>
            <legend>Name</legend>
            <InPlaceText
              defaultValue={ExerciseStore.showData.name}
              model='exercises'
              field='name'
              id={ExerciseStore.showData.id}
            />
            {
              WorksheetStore.hasShowData && ExerciseStore.hasShowData &&
              <Link
                to={{name: 'worksheets.exercises.show', params: {worksheetId: WorksheetStore.showData.id, exerciseId: ExerciseStore.showData.id}}}
                className='underline'
                style={{fontSize: 12}}
              >
                Go to Exercise
              </Link>
            }
          </fieldset>
          <fieldset style={{height: '100%'}}>
            <legend>{`Exercise Navigation (${ExerciseUI.exerciseIndex + 1}/${WorksheetStore.showData?.exercises?.length})`}</legend>
            <NavigationButtonRow>
              <ButtonPrimary
                disabled={ExerciseUI.exerciseIsFirst}
                style={{width: '50%'}}
                onClick={() => ExerciseUI.goToPreviousExercise(noop, 'creator.exercises.edit')}
              >
                <i className='fa fa-arrow-left' /> Previous
              </ButtonPrimary>
              <ButtonPrimary
                disabled={ExerciseUI.exerciseIsLast}
                style={{width: '50%'}}
                onClick={() => ExerciseUI.goToNextExercise(noop, 'creator.exercises.edit')}
              >
                Next <i className='fa fa-arrow-right' />
              </ButtonPrimary>
            </NavigationButtonRow>
          </fieldset>
          <fieldset>
            <legend>Exercise Type</legend>
            <div
              data-tip={this.disableChangingExerciseType ? '' : undefined}
              data-for={this.disableChangingExerciseType ? 'exercise-type-tooltip' : undefined}
            >
              <InPlaceSelect
                model='exercises'
                field='exercise_type_id'
                id={ExerciseStore.showData.id}
                options={ExerciseTypeStore.indexData.map(item => [item.id, item.name])}
                value={ExerciseStore.showData.exerciseTypeId}
                afterChange={this.getExercise}
                disabled={this.disableChangingExerciseType}
              />
            </div>
            {this.disableChangingExerciseType && (
              <ReactTooltip
                id='exercise-type-tooltip'
                place='bottom'
              >
                <div>Please delete all exercise entries before changing the exercise type</div>
              </ReactTooltip>
            )}
          </fieldset>
        </Grid>
        <Grid columns={2}>
          <fieldset>
            <legend>Tags</legend>
            <TagSection>
              <GrayButton onClick={() => this.editTags = true}>
                <i className='fa fa-plus' />
                Edit Tags
              </GrayButton>
                <TagWrapper>
                  {
                    ExerciseStore.hasShowData && ExerciseStore.showData.exerciseTags.map(item => {
                    return (
                      <Tag key={item.id}>
                        {item.tag.name}
                      </Tag>
                    );
                  })
                }
              </TagWrapper>
            </TagSection>
          </fieldset>
          <fieldset style={{height: '100%'}}>
            <legend>Ratings</legend>
            <div className='ratings-section' style={{fontSize: '15px'}}>
              <span>{`Average: ${ExerciseStore.showData.ratingsAverage}`}</span>
              <span style={{marginLeft: '40px'}}>{`Number of ratings: ${ExerciseStore.showData.ratingsCount}`}</span>
            </div>
              {
                ExerciseStore.showData.ratingsWithCommentsCount > 0 &&
                  <div
                    style={{marginTop: '5px', fontSize: '15px', display: 'flex', alignItems: 'center', cursor: 'pointer'}}
                    onClick={() => this.showRatingComments = true}
                  >
                    <i className='fa fa-plus'/><span style={{marginLeft: '10px'}}>{`See the ${ExerciseStore.showData.ratingsWithCommentsCount} comment${ExerciseStore.showData.ratingsWithCommentsCount > 1 ? 's' : ''}`}</span>
                  </div>
              }
              {
                ExerciseStore.showData.ratingsWithCommentsCount === 0 &&
                  <div
                    style={{marginTop: '5px', fontSize: '13px', display: 'flex', alignItems: 'center'}}
                  >
                    No users have left a comment yet
                  </div>
              }
          </fieldset>
        </Grid>
        <div className='backend-title'>
          Description
        </div>
        <fieldset>
          <legend>Default Description</legend>
          {
            isPresent(ExerciseStore.showData.defaultDescription) &&
              <ButtonBorder>
                <ButtonPrimary
                  onClick={() => this.clearField('default_description')}
                >
                  Clear
                </ButtonPrimary>
              </ButtonBorder>
          }
          <InPlaceText
            richText
            defaultValue={ExerciseStore.showData.defaultDescription}
            inputType='textarea'
            model='exercises'
            field='default_description'
            id={ExerciseStore.showData.id}
            afterChange={this.getExercise}
            fontSize='16px'
          />
        </fieldset>
        <fieldset>
          <legend>Expanded Description <InfoTooltip>This description will only be shown if the user chooses to expand the description.</InfoTooltip></legend>
          {
            isPresent(ExerciseStore.showData.expandedDescription) &&
              <ButtonBorder>
                <ButtonPrimary
                  onClick={() => this.clearField('expanded_description')}
                >
                  Clear
                </ButtonPrimary>
              </ButtonBorder>
          }
          <InPlaceText
            richText
            defaultValue={ExerciseStore.showData.expandedDescription}
            inputType='textarea'
            model='exercises'
            field='expanded_description'
            id={ExerciseStore.showData.id}
            afterChange={this.getExercise}
            fontSize='16px'
          />
        </fieldset>
        <fieldset>
          <legend>Description Video <InfoTooltip>This can be used for listening comprehension exercises for instance.</InfoTooltip></legend>
          <InPlaceText
            show
            defaultValue={ExerciseStore.showData.descriptionVideo}
            placeholder='https://www.youtube.com/watch?v=dQw4w9WgXcQ'
            model='exercises'
            field='description_video'
            id={ExerciseStore.showData.id}
            afterChange={this.getExercise}
            fontSize='16px'
          />
        </fieldset>
        <fieldset>
          <legend>
            PDF Description
            <InfoTooltip>
              <div>This description will only be displayed in the PDF version of the exercise</div>
              <div>instead of the Default Description & Expanded Description.</div>
            </InfoTooltip>
          </legend>
          {
            isPresent(ExerciseStore.showData.pdfDescription) &&
              <ButtonBorder>
                <ButtonPrimary
                  onClick={() => this.clearField('pdf_description')}
                >
                  Clear
                </ButtonPrimary>
              </ButtonBorder>
          }
          <InPlaceText
            richText
            defaultValue={ExerciseStore.showData.pdfDescription}
            inputType='textarea'
            model='exercises'
            field='pdf_description'
            id={ExerciseStore.showData.id}
            afterChange={this.getExercise}
          />
        </fieldset>
        {
          ExerciseStore.showData?.exerciseType?.slug !== 'simpleTextPrompt' &&
            <>
              <div className='backend-title'>Exercise Entries</div>
              {
                ExerciseStore.hasShowData && ExerciseStore.showData.hasExerciseType && isPresent(ExerciseStore.showData.exerciseType.slug) &&
                  <ExerciseEntryCreate
                    showGrammarTip
                    exercise={ExerciseStore.showData}
                    afterCreate={this.getExercise}
                    languageId={AppUI.groupId === Constants.EASY_GERMAN_GROUP_ID ? 'DE' : null}
                  />
              }
              {
                ExerciseStore.hasShowData && ExerciseStore.showData.hasExerciseType && isPresent(ExerciseStore.showData.exerciseType.slug) &&
                  <fieldset>
                    <legend>{`Entries (${ExerciseStore.showData.exerciseEntriesCount})`}</legend>
                    {
                      ExerciseStore.showData.exerciseEntriesCount > 0 &&
                      <EntriesSettingsRow>
                        {
                          !this.randomizing &&
                            <ButtonPrimary
                              disabled={ExerciseStore.showData.exerciseEntriesCount === 1}
                              onClick={this.randomizeOrder}
                            >
                              <i className='fa fa-random'/>
                              Randomize Order of Entries
                            </ButtonPrimary>
                        }
                        { this.randomizing && <Spinner margin='8px 0'/> }
                        {
                          ExerciseStore.showData.exerciseType.slug === 'wordOptions' &&
                            <div style={{display: 'flex', alignItems: 'center', gap: '3px'}}>
                              <Text
                                fontSize='14px'
                                color={this.optionsDisplayedOptions.length <= 1 ? 'gray' : 'black'}
                              >
                                Number of Options:
                              </Text>
                              <InPlaceSelect
                                model='exercises'
                                field='options_displayed'
                                id={ExerciseStore.showData.id}
                                options={this.optionsDisplayedOptions}
                                value={ExerciseStore.showData.optionsDisplayed}
                                style={{height: '30px'}}
                                disabled={this.optionsDisplayedOptions.length <= 1}
                              />
                            </div>
                        }
                        <InPlaceCheckbox
                          model='exercises'
                          field='show_tip_icon'
                          value={ExerciseStore.showData.showTipIcon}
                          id={ExerciseStore.showData.id}
                          afterChange={this.getExercise}
                        >
                          Show Tip Icon
                        </InPlaceCheckbox>
                        {
                          !ExerciseStore.showData.exerciseType.hideTarget &&
                            <InPlaceCheckbox
                              model='exercises'
                              field='capitalize_entries'
                              value={ExerciseStore.showData.capitalizeEntries}
                              id={ExerciseStore.showData.id}
                              afterChange={this.getExercise}
                            >
                              Capitalize Entries
                              <InfoTooltip position='left'>
                                <div>This will ensure the first word of each sentence is capitalized when selected by the user.</div>
                                <div>If your entries are not sentences, you should untick this option</div>
                              </InfoTooltip>
                            </InPlaceCheckbox>
                        }
                        { ExerciseStore.showData.exerciseType.hideTarget && <div /> }
                      </EntriesSettingsRow>
                    }
                    <ExerciseEntryIndex
                      exercise={ExerciseStore.showData}
                      exerciseType={ExerciseStore.showData.exerciseType}
                      afterUpdate={this.getExercise}
                      languageId={AppUI.groupId === Constants.EASY_GERMAN_GROUP_ID ? 'DE' : null}
                      hideInstruction
                      showGrammarTips
                    />
                  </fieldset>
              }
            </>
        }
        {
          ExerciseStore.requestCounter > 0 &&
          <Spinner />
        }
      </Stack>
    );
  }
}

export default ExerciseEdit;
